import {
  Alert, Box, Button, Chip, IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment';
import { FC, useState } from 'react';

import config from '../../../../config';
import useUser from '../../../../hooks/useUser';
import { formatInr } from '../../utils/format';
import { useGargi } from '../AskGargi/context';

export interface GargiNote {
  id: string;
  kind: 'business' | 'data' | 'preference' | 'idea';
  kindLabel: string;
  title: string;
  text: string;
  value: number | null;
  question: string;
  created: number;
}

const KEY = 'gargi-notes';
const getNotes = () => axios.get<{ notes: GargiNote[] }>(`${config.apiServerUrl}/users/intelligence/notes`).then(({ data }) => data.notes);
export const deleteNote = (id: string) => axios.delete(`${config.apiServerUrl}/users/intelligence/notes/${id}`);
export const addNote = (note: { kind: GargiNote['kind']; title?: string; text: string }) =>
  axios.post<GargiNote>(`${config.apiServerUrl}/users/intelligence/notes`, note).then(({ data }) => data);

// What Gargi has learned about this business, shared by the header button, the ideas section and Ask Gargi's answers.
export const useGargiNotes = () => {
  const { selectedBusiness } = useUser();
  const queryClient = useQueryClient();
  const notes = useQuery({ queryKey: [KEY, selectedBusiness?.id], queryFn: getNotes, enabled: !!selectedBusiness?.id, staleTime: 60_000 });
  const remove = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: [KEY] });
  return { notes: notes.data ?? [], isLoading: notes.isLoading, remove, refresh };
};

const ORDER: GargiNote['kind'][] = ['idea', 'business', 'preference', 'data'];

const ADDABLE: { kind: GargiNote['kind']; label: string }[] = [
  { kind: 'business', label: 'About the business' }, { kind: 'data', label: 'About the data' }, { kind: 'preference', label: 'How to answer' },
];

// Everything Gargi remembers about the business, inside Ask Gargi: each note deletable, and the team can add their
// own (e.g. "Diwali sale starts the week before Diwali"). Gargi sends only the notes that matter for each question.
export const MemoryPanel: FC = () => {
  const { notes, remove, refresh } = useGargiNotes();
  const [kind, setKind] = useState<GargiNote['kind']>('business');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () => addNote({ kind, title: title.trim() || undefined, text: text.trim() }),
    onSuccess: () => { setTitle(''); setText(''); refresh(); },
  });
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant='overline' color='text.secondary'>What Gargi remembers</Typography>
        <Typography variant='body2' color='text.secondary'>
          Learned from questions about this business, plus what your team adds. Gargi uses the notes that matter for each question.
        </Typography>
      </Box>
      <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap>
            {ADDABLE.map((k) => (
              <Chip key={k.kind} size='small' label={k.label} color={kind === k.kind ? 'primary' : 'default'}
                variant={kind === k.kind ? 'filled' : 'outlined'} onClick={() => setKind(k.kind)} />
            ))}
          </Stack>
          <TextField size='small' placeholder='Title (optional)' value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 120 }} />
          <TextField size='small' multiline minRows={2} placeholder='e.g. Our Diwali sale starts the week before Diwali; WEB and Mobile_App are online orders'
            value={text} onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 400 }} />
          <Stack direction='row' justifyContent='flex-end'>
            <Button size='small' variant='contained' disableElevation disabled={text.trim().length < 3 || add.isPending} onClick={() => add.mutate()}>
              {add.isPending ? 'Saving…' : 'Add to memory'}
            </Button>
          </Stack>
          {add.isError && <Alert severity='warning'>That note could not be saved. Try again.</Alert>}
        </Stack>
      </Paper>
      {!notes.length && <Alert severity='info'>Nothing yet. As you ask questions, Gargi keeps what is worth knowing next time.</Alert>}
      {ORDER.map((k) => {
        const group = notes.filter((n) => n.kind === k);
        if (!group.length) return null;
        return (
          <Box key={k}>
            <Typography variant='overline' color='text.secondary'>{group[0].kindLabel}</Typography>
            <Stack spacing={1}>
              {group.map((n) => (
                <Paper key={n.id} variant='outlined' sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction='row' spacing={1} alignItems='flex-start' justifyContent='space-between'>
                    <Box sx={{ minWidth: 0 }}>
                      {n.title && <Typography variant='body2' fontWeight={700}>{n.title}{n.value ? ` · ${formatInr(n.value)}` : ''}</Typography>}
                      <Typography variant='body2'>{n.text}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {moment.unix(n.created).fromNow()}{n.question ? ` · ${n.question === 'Added by the team' ? 'added by the team' : `from “${n.question.slice(0, 80)}${n.question.length > 80 ? '…' : ''}”`}` : ''}
                      </Typography>
                    </Box>
                    <IconButton size='small' onClick={() => remove.mutate(n.id)} aria-label='Delete' disabled={remove.isPending}>
                      <DeleteOutlineRoundedIcon fontSize='small' />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

// Opportunities Gargi spotted in conversations, shown with the other opportunities.
export const ConversationIdeas: FC = () => {
  const { notes, remove } = useGargiNotes();
  const gargi = useGargi();
  const ideas = notes.filter((n) => n.kind === 'idea');
  if (!ideas.length) return null;
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant='h6' fontWeight={700}>Ideas from your conversations</Typography>
        <Typography variant='body2' color='text.secondary'>Opportunities Gargi spotted while answering questions about this business.</Typography>
      </Box>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' } }}>
        {ideas.map((n) => (
          <Paper key={n.id} variant='outlined' sx={{ p: 2.5, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
              <Chip size='small' label='From a conversation' sx={{ height: 20 }} />
              <Typography variant='caption' color='text.secondary'>{moment.unix(n.created).fromNow()}</Typography>
            </Stack>
            <Typography variant='subtitle1' fontWeight={700}>{n.title || 'Idea'}</Typography>
            {n.value && <Typography variant='h6' fontWeight={800} color='primary.main'>{formatInr(n.value)}</Typography>}
            <Typography variant='body2' sx={{ flex: 1, mt: 0.5 }}>{n.text}</Typography>
            <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
              {gargi.available && (
<Button size='small' variant='contained' disableElevation
                onClick={() => gargi.open(`Let's act on "${n.title}": ${n.text} Size it again on today's data, then give me the audience, offer, channel and timing.`, { idea: n.id })}>
                Plan with Gargi
              </Button>)}
              <Button size='small' color='inherit' sx={{ color: 'text.secondary' }} onClick={() => remove.mutate(n.id)}>Remove</Button>
            </Stack>
          </Paper>
        ))}
      </Box>
    </Stack>
  );
};
